import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  TextField,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Tooltip,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ApiService from '../../functions/apiService';
import { toast } from 'react-toastify';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ButtonTheme from '../../components/ThemeButton/ThemeButton';

const ROWS_PER_PAGE = 25;

// Composite key for React keys and record identification
const recordKey = (r) =>
  `${r.emp_code}|${r.date}|${r.type}|${r.subtype}`;

const Accruals = () => {
  // ── Data state ──────────────────────────────────────────────
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [subtypeOptions, setSubtypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // ── Filter state ────────────────────────────────────────────
  const [filterEmployee, setFilterEmployee] = useState(null);
  const [filterFromDate, setFilterFromDate] = useState(null);
  const [filterToDate, setFilterToDate] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);

  // ── Modal state ─────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
  const [modalData, setModalData] = useState({
    emp_code: null,
    date: null,
    type: '',
    subtype: '',
    total: '',
    note: '',
  });
  const [modalEmployee, setModalEmployee] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Delete confirmation ─────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Load dropdown data ──────────────────────────────────────
  useEffect(() => {
    ApiService.get('/employee-totals/employees', {}, 'getETEmployees')
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? res;
        setEmployees(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error('Failed to load employees', err));

    ApiService.get('/employee-totals/types', {}, 'getETTypes')
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? res;
        setTypeOptions(data?.types || []);
        setSubtypeOptions(data?.subtypes || []);
      })
      .catch((err) => console.error('Failed to load types', err));
  }, []);

  // ── Fetch records ───────────────────────────────────────────
  const fetchRecords = useCallback(() => {
    setLoading(true);
    const params = {};

    if (filterEmployee) params.emp_code = filterEmployee.id;
    if (filterFromDate) params.from_date = moment(filterFromDate).format('YYYY-MM-DD');
    if (filterToDate) params.to_date = moment(filterToDate).format('YYYY-MM-DD');
    if (filterType) params.type = filterType;

    ApiService.get('/employee-totals', params, 'getEmployeeTotals')
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? res;
        setRecords(Array.isArray(data) ? data : []);
        setPage(1);
      })
      .catch((err) => {
        console.error('Failed to load records', err);
        toast.error('Failed to load accrual records');
      })
      .finally(() => {
        setLoading(false);
        setInitialLoad(false);
      });
  }, [filterEmployee, filterFromDate, filterToDate, filterType]);

  useEffect(() => {
    fetchRecords();
  }, []);

  // ── Local search + pagination ───────────────────────────────
  const filteredRecords = useMemo(() => {
    if (!searchText.trim()) return records;
    const q = searchText.toLowerCase();
    return records.filter(
      (r) =>
        (r.employee_name || '').toLowerCase().includes(q) ||
        (r.type || '').toLowerCase().includes(q) ||
        (r.subtype || '').toLowerCase().includes(q) ||
        (r.note || '').toLowerCase().includes(q)
    );
  }, [records, searchText]);

  const pageCount = Math.ceil(filteredRecords.length / ROWS_PER_PAGE);
  const paginatedRecords = filteredRecords.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE
  );

  // ── Refresh type options after mutations ────────────────────
  const refreshTypes = () => {
    ApiService.get('/employee-totals/types', {}, 'getETTypes')
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? res;
        setTypeOptions(data?.types || []);
        setSubtypeOptions(data?.subtypes || []);
      })
      .catch(() => {});
  };

  // ── Modal handlers ──────────────────────────────────────────
  const openAddModal = () => {
    setModalMode('add');
    setModalData({ emp_code: null, date: null, type: '', subtype: '', total: '', note: '' });
    setModalEmployee(null);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setModalMode('edit');
    setModalData({
      emp_code: record.emp_code,
      date: record.date,
      type: record.type,
      subtype: record.subtype,
      total: record.total ?? '',
      note: record.note ?? '',
    });
    const emp = employees.find((e) => e.id === record.emp_code) || null;
    setModalEmployee(emp);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaving(false);
  };

  const handleSave = () => {
    if (!modalData.emp_code) {
      toast.error('Please select an employee');
      return;
    }
    if (!modalData.date) {
      toast.error('Please select a date');
      return;
    }
    if (!modalData.type.trim()) {
      toast.error('Type is required');
      return;
    }
    if (!modalData.subtype.trim()) {
      toast.error('Subtype is required');
      return;
    }

    setSaving(true);
    const payload = {
      emp_code: modalData.emp_code,
      date: moment(modalData.date).format('YYYY-MM-DD'),
      type: modalData.type.trim(),
      subtype: modalData.subtype.trim(),
      total: modalData.total === '' ? 0 : parseFloat(modalData.total),
      note: modalData.note || null,
    };

    const apiCall =
      modalMode === 'add'
        ? ApiService.post('/employee-totals', payload)
        : ApiService.put('/employee-totals', payload);

    apiCall
      .then((res) => {
        const msg = res?.data?.message || res?.message || 'Saved successfully';
        toast.success(msg);
        closeModal();
        fetchRecords();
        refreshTypes();
      })
      .catch((err) => {
        const msg = err?.data?.message || err?.message || 'Save failed';
        toast.error(msg);
        setSaving(false);
      });
  };

  // ── Delete handlers ─────────────────────────────────────────
  const confirmDelete = (record) => {
    setDeleteTarget(record);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);

    ApiService.post('/employee-totals/delete', {
      emp_code: deleteTarget.emp_code,
      date: deleteTarget.date,
      type: deleteTarget.type,
      subtype: deleteTarget.subtype,
    })
      .then((res) => {
        const msg = res?.data?.message || res?.message || 'Deleted successfully';
        toast.success(msg);
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
        fetchRecords();
      })
      .catch((err) => {
        const msg = err?.data?.message || err?.message || 'Delete failed';
        toast.error(msg);
      })
      .finally(() => setDeleting(false));
  };

  // ── Shared styles ───────────────────────────────────────────
  const inputSx = {
    '& .MuiOutlinedInput-root': { height: '40px', fontSize: '0.875rem' },
    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <Paper className="p-4" style={{ marginTop: '1rem' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Accruals</Typography>
        <ButtonTheme
          type="button"
          variant="filled"
          color="primary"
          style={{ height: 38 }}
          onClick={openAddModal}
        >
          <AddIcon sx={{ fontSize: 18, mr: 0.5 }} />
          Add Record
        </ButtonTheme>
      </Box>

      {/* Filter Bar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 3,
          pb: 2,
          borderBottom: '1px solid #EAECF0',
        }}
      >
        <Autocomplete
          size="small"
          options={employees}
          getOptionLabel={(o) => o.name || ''}
          value={filterEmployee}
          onChange={(_, val) => setFilterEmployee(val)}
          renderInput={(params) => (
            <TextField {...params} label="Employee" placeholder="All" sx={{ minWidth: 200, ...inputSx }} />
          )}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="From Date"
            value={filterFromDate}
            onChange={(val) => setFilterFromDate(val)}
            slotProps={{
              textField: { size: 'small', sx: { width: 155, ...inputSx } },
            }}
          />
          <DatePicker
            label="To Date"
            value={filterToDate}
            onChange={(val) => setFilterToDate(val)}
            slotProps={{
              textField: { size: 'small', sx: { width: 155, ...inputSx } },
            }}
          />
        </LocalizationProvider>

        <Autocomplete
          size="small"
          freeSolo
          options={typeOptions}
          value={filterType}
          onChange={(_, val) => setFilterType(val)}
          onInputChange={(_, val, reason) => {
            if (reason === 'input') setFilterType(val || null);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Type" placeholder="All" sx={{ minWidth: 150, ...inputSx }} />
          )}
        />

        <Button
          variant="contained"
          onClick={fetchRecords}
          disabled={loading}
          sx={{ height: 36, textTransform: 'none', fontWeight: 600 }}
        >
          Apply
        </Button>

        <Button
          variant="text"
          onClick={() => {
            setFilterEmployee(null);
            setFilterFromDate(null);
            setFilterToDate(null);
            setFilterType(null);
            setSearchText('');
          }}
          sx={{ height: 36, textTransform: 'none' }}
        >
          Clear
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        <TextField
          size="small"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 180, ...inputSx }}
        />
      </Box>

      {/* Data Table */}
      {loading || initialLoad ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredRecords.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: '#888' }}>
          <Typography variant="h6">No records found</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Adjust your filters or add a new record.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 850 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', width: 110 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Subtype</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', width: 90 }} align="right">
                    Total
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Note</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', width: 90 }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRecords.map((row) => (
                  <TableRow
                    key={recordKey(row)}
                    hover
                    sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}
                  >
                    <TableCell sx={{ fontSize: '0.8125rem' }}>{row.employee_name}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>
                      {row.date ? moment(row.date).format('MM/DD/YYYY') : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>{row.type}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>{row.subtype}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }} align="right">
                      {row.total != null ? parseFloat(row.total).toFixed(2) : '—'}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.8125rem',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.note || ''}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditModal(row)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => confirmDelete(row)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                shape="rounded"
                size="small"
              />
            </Box>
          )}

          <Typography variant="body2" sx={{ mt: 1, color: '#888', textAlign: 'right' }}>
            {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}
          </Typography>
        </>
      )}

      {/* ── Add / Edit Modal ─────────────────────────────────── */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>{modalMode === 'add' ? 'Add Accrual Record' : 'Edit Accrual Record'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <Autocomplete
              options={employees}
              getOptionLabel={(o) => o.name || ''}
              value={modalEmployee}
              onChange={(_, val) => {
                setModalEmployee(val);
                setModalData((prev) => ({ ...prev, emp_code: val?.id || null }));
              }}
              disabled={modalMode === 'edit'}
              renderInput={(params) => (
                <TextField {...params} label="Employee *" size="small" />
              )}
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date *"
                value={modalData.date ? new Date(modalData.date) : null}
                onChange={(val) =>
                  setModalData((prev) => ({
                    ...prev,
                    date: val ? moment(val).format('YYYY-MM-DD') : null,
                  }))
                }
                disabled={modalMode === 'edit'}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>

            <Autocomplete
              freeSolo
              options={typeOptions}
              value={modalData.type}
              onChange={(_, val) => setModalData((prev) => ({ ...prev, type: val || '' }))}
              onInputChange={(_, val) => setModalData((prev) => ({ ...prev, type: val || '' }))}
              disabled={modalMode === 'edit'}
              renderInput={(params) => (
                <TextField {...params} label="Type *" size="small" placeholder="Select or type a value" />
              )}
            />

            <Autocomplete
              freeSolo
              options={subtypeOptions}
              value={modalData.subtype}
              onChange={(_, val) => setModalData((prev) => ({ ...prev, subtype: val || '' }))}
              onInputChange={(_, val) => setModalData((prev) => ({ ...prev, subtype: val || '' }))}
              disabled={modalMode === 'edit'}
              renderInput={(params) => (
                <TextField {...params} label="Subtype *" size="small" placeholder="Select or type a value" />
              )}
            />

            <TextField
              label="Total"
              type="number"
              size="small"
              value={modalData.total}
              onChange={(e) => setModalData((prev) => ({ ...prev, total: e.target.value }))}
              inputProps={{ step: '0.01' }}
            />

            <TextField
              label="Note"
              size="small"
              multiline
              minRows={2}
              maxRows={4}
              value={modalData.note}
              onChange={(e) => setModalData((prev) => ({ ...prev, note: e.target.value }))}
              inputProps={{ maxLength: 255 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving ? <CircularProgress size={20} /> : modalMode === 'add' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ────────────────────────────────── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Typography variant="body2">
              Are you sure you want to delete the{' '}
              <strong>{deleteTarget.type} / {deleteTarget.subtype}</strong> record for{' '}
              <strong>{deleteTarget.employee_name}</strong> on{' '}
              <strong>{moment(deleteTarget.date).format('MM/DD/YYYY')}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: 'none' }}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Accruals;
